// Fellowship over the real API (biblestandard-api, docs/fellowship/05-BACKEND-API.md), through the
// typed client. It maps the API's shapes onto the app's (types.ts) so the screens are the same on
// the prototype data and on the API. The reader is always the session's: no request here names a
// user as authority; user ids only ever name the *other* person (a friend, someone to block).
// Scripture text never comes from the API: references resolve against the WEB in bible.ts.
import { createApi, type ApiClient } from "../api/client";
import type { components } from "../api/schema";
import { lookupWeb } from "./bible";
import { parsePassage } from "./books";
import { ApiError, networkError, toApiError } from "./problem";
import type { FellowshipSource, Page, ReportReason, Session } from "./source";
import type { AuthProviders, Comment, Config, Friends, ImageUpload, NewPost, Notification, Post, PostImage, Profile, ProfileDetailsInput, ProfileSettings, Relation, UserCard, Votd } from "./types";

type S = components["schemas"];
type Result<T> = { data?: T; error?: unknown; response: Response };

/** The data of a successful response, or an ApiError with a message a reader can act on. */
export async function ok<T>(p: Promise<Result<T>>): Promise<T> {
  let r: Result<T>;
  try {
    r = await p;
  } catch {
    throw networkError();
  }
  if (!r.response.ok) throw toApiError(r.response, r.error);
  return r.data as T;
}

/** Not there, or not visible to this reader (the API answers both with 404), or not an id at all: null. */
async function orNull<T>(p: Promise<Result<T>>): Promise<T | null> {
  try {
    return await ok(p);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 400)) return null;
    throw e;
  }
}

// ---- Mapping: API shape → app shape ----

/**
 * Photos are served by the API at an absolute address on its own origin, with
 * Cross-Origin-Resource-Policy: same-origin. Loaded through the base the app talks to (the dev proxy
 * locally), they stay same-origin. Set by createApiSource.
 */
let avatarBase: string | null = null;
export const avatarSrc = (url: string | null): string | null => {
  if (!url || avatarBase === null) return url;
  const m = url.match(/^https?:\/\/[^/]+(\/v1\/(?:avatars|post-images)\/.*)$/);
  return m ? `${avatarBase}${m[1]}` : url;
};

/** A post's photo (FW-007), its address on the base the app talks to (the dev proxy locally), so the session cookie goes with it. */
export const toImage = (i: S["PostImage"]): PostImage => ({ id: i.id, url: avatarSrc(i.url) ?? i.url, alt: i.alt, width: i.width, height: i.height, contentType: i.contentType });

export const toCard = (u: S["UserCard"]): UserCard => ({ id: u.id, handle: u.handle, displayName: u.displayName, avatarUrl: avatarSrc(u.avatarUrl) });

export const toRelation = (r: S["ProfileView"]["relation"]): Relation => (r === "friend" ? "friends" : r);

export function toPost(p: S["Post"]): Post {
  return {
    id: p.id,
    author: toCard(p.author),
    kind: p.kind,
    visibility: p.visibility,
    body: p.body,
    title: p.title,
    ref: p.ref,
    translation: p.translation,
    quote: p.quote,
    work: p.work,
    source: p.source ?? null,
    votdKey: p.votdKey,
    reactions: { ...p.reactions },
    myReaction: p.myReaction,
    commentCount: p.commentCount,
    saved: p.saved,
    createdAt: p.createdAt,
    editedAt: p.editedAt,
    canEdit: p.canEdit,
    canDelete: p.canDelete,
    images: (p.images ?? []).map(toImage),
  };
}

export const toComment = (c: S["Comment"]): Comment => ({
  id: c.id,
  postId: c.postId,
  author: toCard(c.author),
  body: c.body,
  createdAt: c.createdAt,
  canDelete: c.canDelete,
});

export const toNotification = (n: S["Notification"]): Notification => ({
  id: n.id,
  kind: n.kind,
  actor: n.actor ? toCard(n.actor) : null,
  others: n.others,
  postId: n.postId,
  journeyId: n.journeyId,
  excerpt: n.excerpt,
  detail: n.detail,
  reaction: n.reaction ?? null,
  createdAt: n.createdAt,
  read: n.read,
});

/** The day's reference and counts from the API, its text from the reader's Bible (the WEB here). */
export function toVotd(v: S["Votd"], text: string | null): Votd {
  return {
    key: v.date,
    ref: { key: v.ref.key, label: v.ref.label },
    translation: "WEB",
    quote: text ?? "",
    counts: { ...v.counts },
    liked: v.liked,
    // Not in the API (yet): the reflection and the passages alongside are editorial content.
    reflection: null,
    alongside: [],
  };
}

/** Your own FS-136 details as the API returns them (GET /v1/me, PUT /v1/me/profile). */
export function toProfileSettings(p: S["OwnProfile"]): ProfileSettings {
  return { details: { ...p.details }, locationVisibility: p.locationVisibility, faithVisibility: p.faithVisibility, faithConsentAt: p.faithConsentAt };
}

/**
 * FS-136: every new account has a temporary profile, so "no profile" no longer means new. A profile
 * counts as onboarded once `complete` (own handle, name, 13+); an older API without the field means
 * finished. The temporary handle and placeholder name are never shown: the reader goes to /welcome.
 */
export const isFinishedProfile = (p: S["Me"]["profile"]) => !!p && p.complete !== false;

export function toSession(me: S["Me"]): Session {
  const p = me.profile;
  if (!p || !isFinishedProfile(p)) return { user: { id: me.id, handle: "", displayName: "", avatarUrl: null }, onboarded: false, email: me.email, fellowshipEnabled: false, inviteCode: null };
  return {
    user: { id: p.id, handle: p.handle, displayName: p.displayName, avatarUrl: avatarSrc(p.avatarUrl) },
    onboarded: true,
    email: me.email,
    fellowshipEnabled: p.fellowshipEnabled,
    inviteCode: p.inviteCode ?? null,
    // Older APIs have no details: treated as none.
    ...(p.details ? { profile: toProfileSettings(p) } : {}),
  };
}

/** The reader's local calendar day: the verse of the day follows the reader's day, as on iOS. */
export function localDate(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [head = "", data = ""] = dataUrl.split(",");
  const type = head.match(/^data:([^;,]+)/)?.[1] ?? "application/octet-stream";
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

export type ApiSourceOptions = { baseUrl: string; fetch?: typeof fetch };

export function createApiSource(opts: ApiSourceOptions): FellowshipSource {
  const api: ApiClient = createApi(opts);
  avatarBase = opts.baseUrl;
  const doFetch = opts.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));

  /** Better Auth's routes (/v1/auth/*) aren't in the OpenAPI document; they're called directly. */
  async function auth<T = unknown>(path: string, body: unknown, credentials: RequestCredentials = "include"): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(
        new Request(`${opts.baseUrl}/v1/auth${path}`, {
          method: "POST",
          credentials,
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body ?? {}),
        }),
      );
    } catch {
      throw networkError();
    }
    const text = await res.text();
    const json: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) throw toApiError(res, json);
    return json as T;
  }

  // Kept between calls: the day's verse as the API names it (a repost needs its key), and a photo
  // chosen during onboarding, before there's a profile to put it on.
  let lastVotd: { api: S["Votd"]; text: string | null } | null = null;
  let providers: Promise<AuthProviders> | null = null;
  let onboarded = false;
  let pendingAvatar: string | null = null;

  const me = async () => ok(api.GET("/v1/me"));

  async function putProfile(change: { displayName?: string; bio?: string | null; fellowshipEnabled?: boolean }) {
    const m = await me();
    const p = m.profile as S["Profile"] | null;
    if (!p) throw new ApiError("Finish setting up your profile first.", "no-profile", 409);
    await ok(
      api.PUT("/v1/me/profile", {
        body: {
          handle: p.handle,
          displayName: change.displayName ?? p.displayName,
          bio: change.bio === undefined ? p.bio : change.bio,
          fellowshipEnabled: change.fellowshipEnabled ?? p.fellowshipEnabled,
          ageConfirmed: false, // already confirmed when the profile was made; the API keeps it
        },
      }),
    );
  }

  async function uploadAvatar(dataUrl: string) {
    const blob = dataUrlToBlob(dataUrl);
    await ok(
      api.PUT("/v1/me/avatar", {
        body: blob as unknown as string,
        bodySerializer: (b: unknown) => b as Blob,
        headers: { "content-type": blob.type },
      }),
    );
  }

  async function votdFrom(v: S["Votd"]): Promise<Votd> {
    const text = lastVotd?.api.ref.key === v.ref.key ? lastVotd.text : ((await lookupWeb(v.ref.key))?.text ?? null);
    lastVotd = { api: v, text };
    return toVotd(v, text);
  }

  async function currentVotd() {
    if (!lastVotd || lastVotd.api.date !== localDate()) await votdFrom(await ok(api.GET("/v1/votd/{date}", { params: { path: { date: localDate() } } })));
    return lastVotd!;
  }

  async function getPost(id: string) {
    const p = await orNull(api.GET("/v1/posts/{id}", { params: { path: { id } } }));
    return p ? toPost(p) : null;
  }

  async function mustPost(id: string) {
    const p = await getPost(id);
    if (!p) throw new ApiError("This post isn’t available any more.", "not-found", 404);
    return p;
  }

  /** Every page of a cursor list, up to a sensible cap. */
  async function all<T>(page: (cursor?: string) => Promise<{ items: T[]; nextCursor: string | null }>, maxPages = 10): Promise<T[]> {
    const out: T[] = [];
    let cursor: string | undefined;
    for (let i = 0; i < maxPages; i++) {
      const r = await page(cursor);
      out.push(...r.items);
      if (!r.nextCursor) break;
      cursor = r.nextCursor;
    }
    return out;
  }

  return {
    async config(): Promise<Config> {
      const c = await ok(api.GET("/v1/config"));
      const social = c.features.social ?? { state: "off" as const };
      return { social: { state: social.state, ...(social.message ? { message: social.message } : {}) }, features: c.features };
    },

    async session() {
      const r = await orNull(api.GET("/v1/me")).catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      });
      const s = r ? toSession(r) : null;
      onboarded = !!s?.onboarded;
      return s;
    },
    async sendCode(email) {
      await auth("/email-otp/send-verification-otp", { email: email.trim(), type: "sign-in" });
    },
    async verifyCode(email, code) {
      await auth("/sign-in/email-otp", { email: email.trim(), otp: code });
      const m = await me();
      onboarded = isFinishedProfile(m.profile);
      return { isNew: !onboarded };
    },
    async signOut() {
      await auth("/sign-out", {});
      lastVotd = null;
      onboarded = false;
    },
    authProviders() {
      // The API doesn't list its providers, so each is asked once, signed out (no cookie), on the
      // linking route: an unconfigured provider answers 503 provider-not-configured before anything
      // else; a configured one goes on to ask for a session (401). Nothing is started either way.
      providers ??= (async () => {
        const probe = async (provider: "google" | "apple") => {
          try {
            await auth("/link-social", { provider, callbackURL: `${location.origin}/settings` }, "omit");
            return true;
          } catch (e) {
            return !(e instanceof ApiError && (e.type === "provider-not-configured" || e.type === "network"));
          }
        };
        const [google, apple] = await Promise.all([probe("google"), probe("apple")]);
        return { google, apple };
      })();
      return providers;
    },
    async startSocial(provider, { callbackURL, errorCallbackURL, link }) {
      const r = await auth<{ url?: string }>(link ? "/link-social" : "/sign-in/social", { provider, callbackURL, ...(errorCallbackURL ? { errorCallbackURL } : {}) });
      if (!r.url) throw new ApiError("That sign-in didn’t start. Try again.", "no-url", 502);
      return r.url;
    },
    async signInMethods() {
      return (await ok(api.GET("/v1/me/accounts"))).accounts;
    },
    async removeSignInMethod(provider) {
      await ok(api.DELETE("/v1/me/accounts/{provider}", { params: { path: { provider } } }));
    },
    async devices() {
      return (await ok(api.GET("/v1/me/sessions"))).sessions;
    },
    async signOutDevice(id) {
      await ok(api.DELETE("/v1/me/sessions/{id}", { params: { path: { id } } }));
    },
    async signOutOtherDevices() {
      await ok(api.DELETE("/v1/me/sessions"));
    },
    async handleAvailable(handle) {
      const r = await ok(api.GET("/v1/handles/{handle}/available", { params: { path: { handle } } }));
      return r.available;
    },
    async completeOnboarding({ handle, displayName, ageConfirmed, fellowshipEnabled, details }) {
      await ok(api.PUT("/v1/me/profile", { body: { handle, displayName, bio: null, fellowshipEnabled, ageConfirmed, ...((details ?? {}) as Partial<S["ProfileInput"]>) } }));
      onboarded = true;
      if (pendingAvatar && fellowshipEnabled) {
        const a = pendingAvatar;
        pendingAvatar = null;
        // The profile is made; a refused photo shouldn't undo that, so it's reported, not thrown.
        await uploadAvatar(a).catch(() => undefined);
      }
    },
    async updateProfile({ displayName, bio }) {
      await putProfile({ displayName, bio: bio.trim() || null });
    },
    async setFellowship(on) {
      await putProfile({ fellowshipEnabled: on });
    },
    async profileOptions() {
      return ok(api.GET("/v1/profile-options"));
    },
    async updateProfileDetails(input: ProfileDetailsInput) {
      const m = await me();
      const p = m.profile;
      if (!isFinishedProfile(p)) throw new ApiError("Finish setting up your profile first.", "no-profile", 409);
      // Every other field is sent as it is: the API keeps a detail it isn't sent.
      const saved = await ok(
        api.PUT("/v1/me/profile", { body: { handle: p.handle, displayName: p.displayName, bio: p.bio, fellowshipEnabled: p.fellowshipEnabled, ageConfirmed: false, ...(input as Partial<S["ProfileInput"]>) } }),
      );
      return toProfileSettings(saved);
    },
    async setAvatar(dataUrl) {
      if (!onboarded) {
        pendingAvatar = dataUrl;
        return;
      }
      if (dataUrl === null) await ok(api.DELETE("/v1/me/avatar"));
      else await uploadAvatar(dataUrl);
    },
    exportData: () => ok(api.GET("/v1/me/export")),
    async deleteAccount() {
      await ok(api.DELETE("/v1/me", { body: { confirm: "DELETE" } }));
      onboarded = false;
    },

    async votd() {
      return votdFrom(await ok(api.GET("/v1/votd/{date}", { params: { path: { date: localDate() } } })));
    },
    async likeVotd(liked) {
      const date = (await currentVotd()).api.date;
      const params = { params: { path: { date } } };
      return votdFrom(await ok(liked ? api.PUT("/v1/votd/{date}/like", params) : api.DELETE("/v1/votd/{date}/like", params)));
    },
    async shareVotd() {
      const date = (await currentVotd()).api.date;
      return votdFrom(await ok(api.POST("/v1/votd/{date}/share", { params: { path: { date } } })));
    },

    async feed(cursor): Promise<Page<Post>> {
      const r = await ok(api.GET("/v1/feed", { params: { query: { limit: 20, ...(cursor ? { cursor } : {}) } } }));
      return { items: r.items.map(toPost), nextCursor: r.nextCursor };
    },
    async newCount(since) {
      return (await ok(api.GET("/v1/feed/new-count", { params: { query: { since } } }))).count;
    },
    post: getPost,
    async comments(postId) {
      const items = await all((cursor) => ok(api.GET("/v1/posts/{id}/comments", { params: { path: { id: postId }, query: { limit: 50, ...(cursor ? { cursor } : {}) } } })));
      return items.map(toComment);
    },
    async createPost(input: NewPost) {
      const visibility = input.visibility;
      let body: S["PostInput"];
      if (input.kind === "verse") {
        const p = await lookupWeb(input.refKey, 5);
        if (!p) throw new ApiError("We can’t find that passage. Try a reference like Romans 8:28.", "passage", 422);
        const quote = input.quote || p.text;
        if (quote.length > 1000) throw new ApiError("That’s too long to quote. Choose fewer verses, or a phrase.", "quote", 422);
        body = { kind: "verse", visibility, ref: { key: p.key, keyVersion: 1, label: p.label }, translation: "WEB", quote, body: input.body || null };
      } else if (input.kind === "note") {
        const anchor = input.refKey ? await lookupWeb(input.refKey, 5) : null;
        body = { kind: "note", visibility, title: input.title || null, body: input.body, ref: anchor ? { key: anchor.key, keyVersion: 1, label: anchor.label } : null };
      } else if (input.kind === "highlight") {
        if (input.quote.length > 1000) throw new ApiError("That’s too long to quote. Choose a shorter passage.", "quote", 422);
        body = { kind: "highlight", visibility, work: input.work, source: input.source, quote: input.quote, body: input.body || null };
      } else if (input.kind === "votd_repost") {
        const v = await currentVotd();
        if (!v.text) throw new ApiError("Today’s verse isn’t available. Try again shortly.", "votd", 503);
        body = { kind: "votd_repost", visibility, votdKey: v.api.key, ref: v.api.ref, translation: "WEB", quote: v.text, body: input.body || null };
      } else {
        body = { kind: "milestone", visibility, title: input.title, body: input.body || null };
      }
      if (input.images?.length) body = { ...body, images: input.images.map((i) => ({ id: i.id, alt: i.alt.trim() })) };
      return toPost(await ok(api.POST("/v1/posts", { body, headers: { "idempotency-key": crypto.randomUUID() } })));
    },
    /** One photo, the raw bytes (XMLHttpRequest, so the upload can report its progress; fetch can't). */
    uploadPostImage(image, onProgress) {
      return new Promise<ImageUpload>((resolve, reject) => {
        const x = new XMLHttpRequest();
        x.open("POST", `${opts.baseUrl}/v1/post-images`);
        x.withCredentials = true;
        x.setRequestHeader("content-type", image.type);
        x.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress?.(e.loaded / e.total);
        };
        x.onerror = () => reject(networkError());
        x.onload = () => {
          let parsed: unknown = null;
          try {
            parsed = JSON.parse(x.responseText);
          } catch {
            // An empty or non-JSON answer: the status says enough.
          }
          if (x.status === 201 || x.status === 200) {
            const u = parsed as S["PostImageUpload"];
            resolve({ id: u.id, url: avatarSrc(u.url) ?? u.url, width: u.width, height: u.height, contentType: u.contentType, bytes: u.bytes, expiresAt: u.expiresAt });
          } else reject(toApiError(new Response(null, { status: x.status }), parsed));
        };
        x.send(image);
      });
    },
    async deletePost(id) {
      await ok(api.DELETE("/v1/posts/{id}", { params: { path: { id } } }));
    },
    async editPost(id, input) {
      const patch: { body: string | null; title?: string | null } = { body: input.body.trim() || null };
      if (input.title !== undefined) patch.title = input.title.trim() || null;
      return toPost(await ok(api.PATCH("/v1/posts/{id}", { params: { path: { id } }, body: patch })));
    },
    async passagePosts(refKey) {
      // GET /v1/passages/{key}/posts takes a chapter or a verse and finds every post within it. A
      // verse range asks by its first verse; a reading across chapters, by each chapter.
      const p = parsePassage(refKey);
      if (!p) return [];
      const keys =
        p.chapterEnd > p.chapter
          ? Array.from({ length: Math.min(p.chapterEnd - p.chapter + 1, 5) }, (_, i) => `${p.book}.${p.chapter + i}`)
          : [p.from === null ? `${p.book}.${p.chapter}` : `${p.book}.${p.chapter}.${p.from}`];
      const pages = await Promise.all(
        keys.map((key) => all((cursor) => ok(api.GET("/v1/passages/{key}/posts", { params: { path: { key }, query: { limit: 50, ...(cursor ? { cursor } : {}) } } })), 3)),
      );
      const seen = new Set<string>();
      return pages
        .flat()
        .filter((x) => !seen.has(x.id) && seen.add(x.id))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map(toPost);
    },
    async react(postId, reaction) {
      const params = { params: { path: { id: postId } } };
      const r = await ok(reaction ? api.PUT("/v1/posts/{id}/reaction", { ...params, body: { kind: reaction } }) : api.DELETE("/v1/posts/{id}/reaction", params));
      const p = await mustPost(postId);
      return { ...p, reactions: { ...r.reactions }, myReaction: r.myReaction };
    },
    async setSaved(postId, saved) {
      const params = { params: { path: { id: postId } } };
      await ok(saved ? api.PUT("/v1/saves/{id}", params) : api.DELETE("/v1/saves/{id}", params));
      return { ...(await mustPost(postId)), saved };
    },
    async addComment(postId, body) {
      return toComment(await ok(api.POST("/v1/posts/{id}/comments", { params: { path: { id: postId } }, body: { body }, headers: { "idempotency-key": crypto.randomUUID() } })));
    },
    async deleteComment(id) {
      await ok(api.DELETE("/v1/comments/{id}", { params: { path: { id } } }));
    },
    async saved() {
      const items = await all((cursor) => ok(api.GET("/v1/saves", { params: { query: { limit: 50, ...(cursor ? { cursor } : {}) } } })), 5);
      return items.map(toPost);
    },

    async profile(handle): Promise<Profile | null> {
      const p = await orNull(api.GET("/v1/profiles/{handle}", { params: { path: { handle } } }));
      return p ? { ...toCard(p), bio: p.bio, friendCount: p.friendCount, relation: toRelation(p.relation), ...(p.details ? { details: { ...p.details } } : {}) } : null;
    },
    async profilePosts(handle) {
      const r = await orNull(api.GET("/v1/profiles/{handle}/posts", { params: { path: { handle }, query: { limit: 50 } } }));
      return r ? r.items.map(toPost) : [];
    },
    async friends(): Promise<Friends> {
      const [f, r] = await Promise.all([ok(api.GET("/v1/friends")), ok(api.GET("/v1/friends/requests"))]);
      return { friends: f.items.map(toCard), incoming: r.incoming.map(toCard), outgoing: r.outgoing.map(toCard) };
    },
    async findPeople(query) {
      const q = query.trim().replace(/^@/, "").toLowerCase();
      if (q.length < 2) return [];
      const r = await ok(api.GET("/v1/profiles/search", { params: { query: { q: q.slice(0, 21) } } }));
      return r.items.filter((u) => u.relation !== "self").map((u) => ({ ...toCard(u), relation: toRelation(u.relation) }));
    },
    async requestFriend(userId, handle) {
      // The API asks by handle (or invite code), never by id.
      if (!handle) throw new ApiError("Open their profile to ask to be friends.", "handle-needed", 400);
      void userId;
      await ok(api.POST("/v1/friends/requests", { body: { handle } }));
    },
    async requestFriendByInvite(code) {
      await ok(api.POST("/v1/friends/requests", { body: { inviteCode: code } }));
    },
    async acceptFriend(userId) {
      await ok(api.POST("/v1/friends/requests/{userId}/accept", { params: { path: { userId } } }));
    },
    async declineFriend(userId) {
      await ok(api.DELETE("/v1/friends/requests/{userId}", { params: { path: { userId } } }));
    },
    async removeFriend(userId) {
      await ok(api.DELETE("/v1/friends/{userId}", { params: { path: { userId } } }));
    },
    async block(userId) {
      await ok(api.PUT("/v1/blocks/{userId}", { params: { path: { userId } } }));
    },
    async blocks() {
      return (await ok(api.GET("/v1/blocks"))).items.map(toCard);
    },
    async unblock(userId) {
      await ok(api.DELETE("/v1/blocks/{userId}", { params: { path: { userId } } }));
    },

    async notifications() {
      // Opening Activity marks everything read; the list keeps showing what was new on this visit.
      const r = await ok(api.GET("/v1/notifications"));
      if (r.items.some((n) => !n.read)) await ok(api.POST("/v1/notifications/read"));
      return r.items.map(toNotification);
    },
    async unreadCount() {
      return (await ok(api.GET("/v1/notifications/unread-count"))).count;
    },
    async report(target, reason: ReportReason) {
      await ok(api.POST("/v1/reports", { body: { targetKind: target.kind, targetId: target.id, reason }, headers: { "idempotency-key": crypto.randomUUID() } }));
    },

    lookupPassage: (reference, maxVerses) => lookupWeb(reference, maxVerses),
  };
}
